import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSupplier } from "../context/SupplierContext";
import { useSellerProfileDrawer } from "../context/SellerProfileDrawerContext";
import SupplierSwitcher from "./SupplierSwitcher";
import NotificationsBell from "./NotificationsBell";
import api from "../lib/api";
import {
  LayoutDashboard, FileText, CreditCard, Shield, TrendingUp, Truck,
  ShoppingBag, Package, MessageSquare, MessageCircle, Warehouse, BarChart3, Sparkles,
  Plug, Receipt, Store, Users, Heart, Settings as SettingsIcon, LogOut, AppWindow, DollarSign,
  Wallet, Landmark, RefreshCw, ChevronDown, Check, Clock, Activity, Palette, Settings2, Handshake,
  CalendarDays, Inbox, Banknote, Home, ShieldCheck, Eye, Zap, Globe, Lock, AlertTriangle, Gauge, ExternalLink, Gavel, FileSearch, ShieldAlert, Webhook, Building2, PlayCircle,
  HeartPulse, Megaphone, LayoutGrid, Award, Bell, Mail, KeyRound, Menu, ShoppingCart,
  PieChart, List, Boxes, Bot, HandCoins, FilePlus2,
} from "lucide-react";
import { Button } from "./ui/button";
import EnvBanner from "./EnvBanner";
import ModeBanner from "./ModeBanner";
import TestModeBanner from "./TestModeBanner";
import HelpSupportButton from "./HelpSupportButton";
import CommandPalette from "./CommandPalette";
import { VerqoMark } from "./VerqoMark";
import KycGateBlocker from "./KycGateBlocker";
// LegalReAcceptanceGate intentionally NOT mounted — we shipped the
// industry-safe path (change-of-terms clause at signup + non-blocking
// continued-use binding). The component and /api/legal/pending-re-acceptance
// endpoint stay alive so we can resurrect a non-blocking banner later
// without re-plumbing. Do not re-mount the modal without legal sign-off.
import { useAppMode, filterByFeature } from "../context/AppModeContext";
import { useSellerSetup } from "../context/SellerSetupContext";
import { useSellerBilling } from "../context/SellerBillingContext";
import { useSupplierBranding } from "../hooks/useSupplierBranding";

// "Report a bug", "Request a feature" and "Roadmap" used to live as separate
// sidebar entries in the Account section. They've been folded into the
// Help & Support popover (Feb 2026) to keep the sidebar clean — those flows
// are now tabs inside the popover itself. See HelpSupportButton.jsx.

const ICONS = {
  ShoppingBag, Package, MessageSquare, MessageCircle, Warehouse, BarChart3, Sparkles, Plug, Receipt, Store,
};

const CORE_SELLER = [
  { to: "/seller", label: "Dashboard", icon: LayoutDashboard, end: true, feature: "seller_dashboard" },
  { to: "/seller/buyers", label: "Buyers", icon: TrendingUp, feature: "admin_buyers" },
  // Orders is the seller's main flow — invoices/payments are now folded in
  // as the "Awaiting payment" stage so sellers never have to flip tabs.
  // Tab structure (Feb 2026 redesign): 5 consolidated tabs that mirror the
  // top pill-bar in SellerOrders.jsx exactly.
  {
    to: "/seller/orders", label: "Orders", icon: ShoppingCart, feature: "orders",
    children: [
      { hash: "all",       label: "All orders",      icon: List },
      { hash: "action",    label: "Action needed",   icon: Zap },
      { hash: "progress",  label: "In progress",     icon: Activity },
      { hash: "delivered", label: "Delivered",       icon: Check },
      // Claims = Quality Inspection claims (damage/shortage on received goods).
      // Distinct from Insurance below — using AlertTriangle matches the admin
      // portal's convention and avoids icon collision with Insurance.
      { hash: "claims",    label: "Claims",          icon: AlertTriangle },
    ],
  },
  // Payouts (was "Wallet & Payouts") — wallet is now a sub-view.
  {
    to: "/seller/wallet", label: "Payouts", icon: Banknote, feature: "wallet", money: true,
    children: [
      { hash: "overview", label: "Overview", icon: PieChart },
      { hash: "wallet",   label: "Wallet",   icon: Wallet },
      { hash: "credit",   label: "Credit",   icon: Landmark },
      // "Invoices" is a route-style child (its own page at /seller/invoices)
      // rather than a hash sub-tab, because the invoice list has its own
      // table + 5 row-level money actions (Adjust / Credit memo / Sell /
      // Extend credit / Insure) that don't fit cleanly inside the Payouts
      // chrome. Sidebar still nests it under Payouts so the seller's mental
      // model "invoices = records of money I'm owed" holds.
      { to: "/seller/invoices", label: "Invoices", icon: FileText },
      { hash: "methods",  label: "Methods",  icon: CreditCard },
      { hash: "activity", label: "Activity", icon: Clock },
    ],
  },
  { to: "/seller/insurance", label: "Insurance", icon: Shield, feature: "insurance" },
];
const SETTINGS_SELLER = [
  { to: "/seller/settings", label: "Settings", icon: Settings2, feature: "seller_dashboard" },
];
const APPS_SELLER = [
  { to: "/seller/catalog", label: "Catalog", icon: LayoutGrid, app_id: "catalog", feature: "catalog" },
  { to: "/seller/messages", label: "Messages", icon: MessageCircle, app_id: "messages", feature: "catalog" },
  { to: "/seller/dropship", label: "Dropship", icon: Boxes, app_id: "dropship", feature: "dropship" },
  { to: "/seller/rfq", label: "RFQ", icon: MessageSquare, app_id: "rfq", feature: "rfq" },
  { to: "/seller/reports", label: "Reports", icon: BarChart3, app_id: "reports", feature: "reports" },
];

const CORE_BUYER = [
  { to: "/buyer", label: "Dashboard", icon: LayoutDashboard, end: true, feature: "buyer_dashboard" },
  {
    to: "/buyer/orders", label: "Orders", icon: ShoppingCart, feature: "orders",
    // Iter 157 · Orders is a cross-supplier cockpit. Buyers MUST be able to
    // see + take action on every order across every connected supplier in
    // a single place; the page auto-context-switches into the matching
    // supplier when the buyer clicks an action.
    // Tab structure (May 2026 redesign): 6 consolidated tabs.
    // - "Journey" is buyer-only (the educational step-by-step onboarding flow)
    // - The rest mirror SellerOrders. "Action needed" merges old "To pay"
    //   + "Schedule pickup". Old hashes auto-redirect via LEGACY_VIEW_REMAP.
    children: [
      { hash: "journey",  label: "Journey",        icon: Sparkles },
      { hash: "all",      label: "All orders",     icon: List },
      { hash: "action",   label: "Action needed",  icon: Zap },
      { hash: "progress", label: "In progress",    icon: Activity },
      { hash: "done",     label: "Delivered",      icon: Check },
      // Same icon distinction as the seller side — Claims (QI) uses
      // AlertTriangle so it doesn't collide visually with Insurance/Shield.
      { hash: "claims",   label: "Claims",         icon: AlertTriangle },
    ],
  },
  {
    to: "/buyer/payments", label: "Payments", icon: Banknote, feature: "payments_methods", money: true,
    children: [
      { hash: "overview", label: "Overview", icon: PieChart },
      { hash: "wallet",   label: "Wallet",   icon: Wallet },
      { hash: "credit",   label: "Credit",   icon: Landmark },
      { hash: "pre-credit", label: "Pre-credit", icon: HandCoins },
      // Route-style child — mirrors the seller side. Buyer's invoice list
      // lives at /buyer/invoices (a dedicated page) but nests visually under
      // Payments so it's where buyers expect "money I owe" to live.
      { to: "/buyer/invoices", label: "Invoices", icon: FileText },
      { hash: "methods",  label: "Methods",  icon: CreditCard },
      { hash: "activity", label: "Activity", icon: Clock },
    ],
  },
  { to: "/buyer/shipping", label: "Shipping", icon: Truck, feature: "shipping_parcel" },
  { to: "/buyer/score", label: "Score", icon: TrendingUp, feature: "buyer_score" },
  // Integrations moved to Settings → Integrations & API (Feb 2026). Sidebar
  // entry removed to keep navigation lean; entry points now live under
  // Settings and inside the Catalog header CTA.
];

// verqo Hub — value for any buyer (connected or not). Visible to every
// buyer regardless of whether a supplier has invited them.
const HUB_BUYER = [
  { to: "/buyer/po-inbox",      label: "PO Inbox",       icon: Inbox,    feature: "buyer_dashboard" },
  { to: "/buyer/hub/financing", label: "Invoice financing", icon: Banknote, feature: "buyer_dashboard", money: true },
];

// Apps that depend on the connected SELLER having them installed too.
// Buyers can only meaningfully use these when in a seller's storefront IF
// that seller has enabled the same app on their side. Outside of a
// storefront (verqo hub), they're always available.
const CROSS_PORTAL_APP_IDS = new Set(["catalog", "messages", "dropship", "rfq"]);

// All possible apps for buyer — only show installed AND visible.
// Apps marked disabledInHubMode become muted (still visible, not hidden) when
// the buyer has no active supplier — they need a connected seller to be useful.
const APPS_BUYER = [
  // Iter 246 · Catalog icon aligned to seller portal (LayoutGrid) — the
  // buyer side was still using `ShoppingBag` which the global icon audit
  // missed. Every shared nav item now uses the exact same lucide icon on
  // both portals (see TAB_TYPOGRAPHY.md for the codified rule set).
  { to: "/buyer/catalog", label: "Catalog", icon: LayoutGrid, app_id: "catalog", feature: "catalog", disabledInHubMode: true },
  { to: "/buyer/messages", label: "Messages", icon: MessageCircle, app_id: "messages", feature: "catalog", disabledInHubMode: true },
  { to: "/buyer/suppliers", label: "Suppliers", icon: Heart, app_id: "suppliers", feature: "suppliers" },
  { to: "/buyer/dropship", label: "Dropship", icon: Boxes, app_id: "dropship", feature: "dropship", disabledInHubMode: true },
  { to: "/buyer/rfq", label: "RFQ", icon: MessageSquare, app_id: "rfq", feature: "rfq", disabledInHubMode: true },
  { to: "/buyer/inventory", label: "Inventory", icon: Warehouse, app_id: "inventory", feature: "inventory", disabledInHubMode: true },
  { to: "/buyer/po-generator", label: "PO Generator", icon: FilePlus2, app_id: "po_generator", feature: "po_generator", disabledInHubMode: true },
  { to: "/buyer/reports", label: "Reports", icon: BarChart3, app_id: "reports", feature: "reports", disabledInHubMode: true },
  { to: "/buyer/ai", label: "AI Assistant", icon: Bot, app_id: "ai_assistant", feature: "ai_assistant" },
];

const ADMIN_MENU = [
  { to: "/admin", label: "Platform", icon: LayoutDashboard, end: true, feature: "admin_dashboard" },
  { to: "/admin/buyers", label: "Buyers · Scoring", icon: TrendingUp, feature: "admin_buyers" },
  { to: "/admin/underwriting", label: "Underwriting Hub", icon: FileSearch, feature: "admin_buyers" },
  { to: "/admin/risk-engine", label: "AI Risk Engine", icon: Activity, feature: "admin_risk_engine" },
  { to: "/admin/commerce", label: "Commerce", icon: Store, feature: "admin_commerce" },
  { to: "/admin/catalog", label: "Catalog Oversight", icon: LayoutGrid, feature: "admin_commerce" },
  { to: "/admin/claims", label: "QI Claims", icon: AlertTriangle, feature: "admin_commerce" },
  { to: "/admin/wallets", label: "Wallets", icon: Wallet, feature: "admin_finance" },
  { to: "/admin/payments-ops", label: "Money Mission Control", icon: ShieldAlert, feature: "admin_finance" },
  { to: "/admin/shipping", label: "Shipping Console", icon: Truck, feature: "admin_dashboard" },
  { to: "/admin/webhooks", label: "Webhook Explorer", icon: Webhook, feature: "admin_integrations" },
  { to: "/admin/revenue", label: "verqo Revenue", icon: TrendingUp, feature: "admin_finance" },
  { to: "/admin/overrides", label: "Override Audit", icon: ShieldCheck, feature: "admin_finance" },
  { to: "/admin/quotas", label: "Refund Quotas", icon: Gauge, feature: "admin_finance" },
  { to: "/admin/scopes", label: "Admin Roles", icon: ShieldCheck, feature: "admin_dashboard" },
  { to: "/admin/security", label: "Security Center", icon: Shield, feature: "admin_dashboard" },
  { to: "/admin/portal-access", label: "Portal Access", icon: ExternalLink, feature: "admin_dashboard" },
  { to: "/admin/partner-orgs", label: "Partner Orgs", icon: Building2, feature: "admin_dashboard" },
  { to: "/admin/dropship", label: "Dropship Ops", icon: Package, feature: "admin_dropship" },
  { to: "/admin/timeline", label: "User timeline", icon: Activity, feature: "admin_timeline" },
  { to: "/admin/insurance", label: "Insurance", icon: Shield, feature: "insurance" },
  { to: "/admin/master-policy-applications", label: "Master Policies", icon: ShieldCheck, feature: "insurance" },
  { to: "/admin/credit", label: "Credit Approvals", icon: CreditCard, feature: "admin_credit" },
  { to: "/admin/finance", label: "Finance", icon: DollarSign, feature: "admin_finance" },
  { to: "/admin/platform-fees", label: "Platform Fees", icon: DollarSign, feature: "admin_platform_fees" },
  { to: "/admin/support", label: "Support", icon: MessageSquare, feature: "admin_dashboard" },
  { to: "/admin/support-console", label: "Support Console", icon: Sparkles, feature: "admin_dashboard" },
  { to: "/admin/compliance", label: "Compliance", icon: ShieldCheck, feature: "admin_dashboard" },
  { to: "/admin/legal", label: "Legal Queue", icon: Gavel, feature: "admin_dashboard" },
  { to: "/admin/coverage", label: "Coverage Console", icon: Eye, feature: "admin_dashboard" },
  { to: "/admin/operations", label: "Operations", icon: Settings2, feature: "admin_dashboard" },
  { to: "/admin/domains", label: "Domains", icon: Globe, feature: "admin_dashboard" },
  { to: "/admin/integrations", label: "Integrations", icon: Plug, feature: "admin_integrations" },
  { to: "/admin/catalog-api", label: "Catalog API", icon: KeyRound, feature: "admin_integrations" },
  { to: "/admin/users", label: "All users", icon: Users, feature: "admin_users" },
  { to: "/admin/activity", label: "Activity log", icon: Activity, feature: "admin_activity" },
  { to: "/admin/settings", label: "Admin Settings", icon: Shield, feature: "admin_dashboard" },
  // Team notifications — per-event email routing for the verqo internal team
  { to: "/admin/team-notifications", label: "Team Notifications", icon: Bell, feature: "admin_dashboard" },
  { to: "/admin/waitlist", label: "Waitlist", icon: Mail, feature: "admin_dashboard" },
];

// ── Admin sidebar reorganization (Feb 2026 · iter 76) ─────────────────
// 35 flat tabs → 9 collapsible groups, matching the Stripe/Shopify/Linear
// pattern. Each group has 2-6 child routes. The group whose child route
// matches the current pathname auto-expands. `Admin Roles` (/admin/scopes)
// is intentionally absent — it's now reachable via Security Center.
const ADMIN_GROUPS = [
  // Group 0 — "Home" stays as a single, non-collapsible top entry.
  // We render this separately above the groups.
  {
    id: "platform",
    label: "Platform",
    icon: LayoutDashboard,
    is_single: true,
    to: "/admin", end: true,
    feature: "admin_dashboard",
  },
  {
    id: "risk_credit",
    label: "Risk & Credit",
    icon: TrendingUp,
    feature: "admin_buyers",
    children: [
      { to: "/admin/buyers",       label: "Buyers · Scoring", icon: TrendingUp, feature: "admin_buyers" },
      { to: "/admin/seller-trust", label: "Seller Trust",     icon: Award, feature: "admin_buyers" },
      { to: "/admin/underwriting", label: "Underwriting Hub", icon: FileSearch, feature: "admin_buyers" },
      { to: "/admin/risk-engine",  label: "AI Risk Engine",   icon: Activity, feature: "admin_risk_engine" },
      { to: "/admin/credit",       label: "Credit Approvals", icon: CreditCard, feature: "admin_credit" },
      { to: "/admin/insurance",    label: "Insurance",        icon: Shield, feature: "insurance" },
      { to: "/admin/master-policy-applications", label: "Master Policies", icon: ShieldCheck, feature: "insurance" },
      { to: "/admin/coverage",     label: "Coverage Console", icon: Eye, feature: "admin_dashboard" },
    ],
  },
  {
    id: "commerce",
    label: "Commerce",
    icon: Store,
    feature: "admin_commerce",
    children: [
      { to: "/admin/commerce", label: "Orders & Commerce", icon: Store, feature: "admin_commerce" },
      { to: "/admin/catalog",  label: "Catalog Oversight", icon: LayoutGrid, feature: "admin_commerce" },
      { to: "/admin/claims",   label: "QI Claims",         icon: AlertTriangle, feature: "admin_commerce" },
      { to: "/admin/dropship", label: "Dropship Ops",      icon: Package, feature: "admin_dropship" },
      { to: "/admin/shipping", label: "Shipping Console",  icon: Truck, feature: "admin_dashboard" },
    ],
  },
  {
    id: "money",
    label: "Money",
    icon: DollarSign,
    feature: "admin_finance",
    children: [
      { to: "/admin/wallets",        label: "Wallets",                icon: Wallet, feature: "admin_finance" },
      { to: "/admin/payments-ops",   label: "Money Mission Control",  icon: ShieldAlert, feature: "admin_finance" },
      { to: "/admin/bank-rail",      label: "Bank Rail",               icon: Banknote, feature: "admin_finance" },
      { to: "/admin/shield",         label: "Banking rail",            icon: Landmark, feature: "admin_finance" },
      { to: "/admin/trade-score-disputes", label: "Trade Score · Disputes", icon: ShieldAlert, feature: "admin_finance" },
      { to: "/admin/revenue",        label: "verqo Revenue",          icon: TrendingUp, feature: "admin_finance" },
      { to: "/admin/platform-fees",  label: "Platform Fees",          icon: DollarSign, feature: "admin_platform_fees" },
      { to: "/admin/quotas",         label: "Refund Quotas",          icon: Gauge, feature: "admin_finance" },
      { to: "/admin/finance",        label: "Finance (Treasury)",     icon: DollarSign, feature: "admin_finance" },
      { to: "/admin/finance-hub",    label: "Finance Hub",            icon: Banknote, feature: "admin_finance" },
    ],
  },
  {
    id: "security_audit",
    label: "Security & Audit",
    icon: Shield,
    feature: "admin_dashboard",
    children: [
      { to: "/admin/stuck-ops", label: "Stuck Operations", icon: AlertTriangle, feature: "admin_dashboard" },
      { to: "/admin/threat",    label: "Threat Center",   icon: ShieldAlert, feature: "admin_dashboard" },
      { to: "/admin/security",  label: "Security Center", icon: Shield, feature: "admin_dashboard" },
      { to: "/admin/compliance-docs", label: "Compliance Docs", icon: ShieldCheck, feature: "admin_dashboard" },
      { to: "/admin/overrides", label: "Override Audit",  icon: ShieldCheck, feature: "admin_finance" },
      { to: "/admin/activity",  label: "Activity Log",    icon: Activity, feature: "admin_activity" },
      { to: "/admin/timeline",  label: "User Timeline",   icon: Activity, feature: "admin_timeline" },
    ],
  },
  {
    id: "partners",
    label: "Partners & Integrations",
    icon: Building2,
    feature: "admin_dashboard",
    children: [
      { to: "/admin/partner-orgs",  label: "Partner Orgs",     icon: Building2, feature: "admin_dashboard" },
      { to: "/admin/integrations",  label: "Integrations",     icon: Plug, feature: "admin_integrations" },
      { to: "/admin/verqo-shipping-accounts", label: "Verqo Shipping", icon: Package, feature: "admin_integrations" },
      { to: "/admin/catalog-api",   label: "Catalog API",      icon: KeyRound, feature: "admin_integrations" },
      { to: "/admin/webhooks",      label: "Webhook Explorer", icon: Webhook, feature: "admin_integrations" },
      { to: "/admin/portal-access", label: "Portal Access",    icon: ExternalLink, feature: "admin_dashboard" },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    icon: Gavel,
    feature: "admin_dashboard",
    children: [
      { to: "/admin/compliance", label: "Compliance",  icon: ShieldCheck, feature: "admin_dashboard" },
      { to: "/admin/legal",      label: "Legal Queue", icon: Gavel, feature: "admin_dashboard" },
      { to: "/admin/operations", label: "Operations",  icon: Settings2, feature: "admin_dashboard" },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: MessageSquare,
    feature: "admin_dashboard",
    children: [
      { to: "/admin/support",         label: "Tickets",         icon: MessageSquare, feature: "admin_dashboard", badge_key: "support_open" },
      { to: "/admin/support-console", label: "Support Console", icon: Sparkles, feature: "admin_dashboard" },
    ],
  },
  {
    id: "system",
    label: "System",
    icon: Settings2,
    feature: "admin_dashboard",
    children: [
      { to: "/admin/users",            label: "All Users",       icon: Users, feature: "admin_users" },
      { to: "/admin/domains",          label: "Domains",         icon: Globe, feature: "admin_dashboard" },
      { to: "/admin/platform-health",  label: "Platform Health", icon: HeartPulse, feature: "admin_dashboard" },
      { to: "/admin/comms",            label: "Communications",  icon: Megaphone, feature: "admin_dashboard" },
      { to: "/admin/mgmt-hub",         label: "Admin Hub",       icon: LayoutGrid, feature: "admin_dashboard" },
      { to: "/admin/settings",         label: "Admin Settings",  icon: Shield, feature: "admin_dashboard" },
    ],
  },
];

const LABEL = { seller: "Seller Portal", buyer: "Buyer Portal", admin: "Admin Console" };

export default function PortalShell({ role }) {
  const { user, logout } = useAuth();
  const supplier = useSupplier();
  const { open: openProfile } = useSellerProfileDrawer();
  const sellerSetup = useSellerSetup();
  // Seller-only setup gate: when the seller hasn't completed their initial
  // setup (lead time / warehouse / terms / restocking / location), every
  // non-settings sidebar item renders in a "locked" state with a tooltip
  // and click-redirects to /seller/settings. Once they hit "Complete setup"
  // on the Settings page, the gate lifts permanently.
  // Setup-locked state — locks the portal sidebar until tenant is fully
  // configured. ONLY applies to the Owner (who CAN complete setup). Team
  // members (Operators / Viewers / Admins) can't run setup, so we don't
  // gaslight them with the locked banner.
  const setupLocked = role === "seller"
    && sellerSetup?.status && !sellerSetup.status.complete
    && (user?.team_flags?.can_settings !== false);
  // Billing-lock — when the seller's subscription is suspended, collapse
  // the sidebar to a single "Reactivate" link that drops them on the
  // Plan & Billing tab. Read-only state stays navigable (the dashboard
  // banner handles the warning); only full suspension nukes the sidebar.
  // useSellerBilling reads from a context that returns a permissive
  // default outside the seller subtree — safe to call unconditionally.
  // Also gated by the master kill switch — pre-launch the sidebar is
  // never locked regardless of any tenant doc state.
  const sellerBilling = useSellerBilling();
  const billingLocked = role === "seller"
    && sellerBilling?.status?.enforced !== false
    && sellerBilling?.status?.status === "suspended";
  const activeSupplier = role === "buyer" ? supplier.active : null;
  const branding = activeSupplier?.branding || {};
  const nav = useNavigate();
  const location = useLocation();

  // ── Browser-chrome immersion ────────────────────────────────────────
  // When a buyer is operating in a supplier's portal, re-skin the tab
  // title + favicon so they feel "inside the seller's store," not
  // "inside verqo browsing a seller." Derived label uses the segment
  // after `/buyer/` capitalized (Orders, Catalog, Invoices, etc.) —
  // matches what each page is conceptually about without hard-coding
  // page-specific tables.
  const pageLabel = (() => {
    if (role !== "buyer") return undefined;
    const seg = (location.pathname.split("/")[2] || "").trim();
    if (!seg) return undefined;
    return seg
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  })();
  useSupplierBranding(pageLabel);

  const [installedApps, setInstalledApps] = useState([]);
  const [comingSoonAppIds, setComingSoonAppIds] = useState([]);
  const [counts, setCounts] = useState({});  // { 'orders.confirm': 3, 'orders.pay': 2, ... }
  const [messagesUnread, setMessagesUnread] = useState(0);
  const [supportOpen, setSupportOpen] = useState(0);  // admin only — live open-ticket count
  // Iter 218 · mobile drawer state — sidebar becomes an overlay
  // drawer on <md screens (below tablet). Toggled via hamburger.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Close the drawer on route change so a nav click doesn't leave it
  // hanging open over the new page.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  // Seller-only feature flag (tenant.settings.profile.offer_seller_arranged_shipping).
  // Drives the conditional "Shipments" sidebar entry in CORE_SELLER — when off,
  // the entry is omitted; when on, the seller gets a Shipments tab that mirrors
  // (but is independent of) the buyer's BuyerShipping page. See SellerShipping.jsx.
  const [sellerArrangesShipping, setSellerArrangesShipping] = useState(false);
  // Per-parent dropdown expand state. undefined = follow active route default;
  // Sidebar parent-group expansion · accordion model (Jun 2026, fixed Feb 2026).
  //
  // Three states for `expandedKey`:
  //   • null           → follow the route-active default (`isOn` decides)
  //   • "<groupKey>"   → THIS group is explicitly open, all others closed
  //   • ALL_COLLAPSED  → every group is explicitly closed (user collapsed the
  //                      route-active group by clicking its own chevron)
  //
  // The ALL_COLLAPSED sentinel is critical: without it, clicking the
  // already-expanded route-active group's chevron would set expandedKey
  // to that group's key — which still resolves to "expanded" via the
  // matching-key branch, so the group stays open. Users hit this bug
  // because clicking the same tab a second time appeared to do nothing.
  //
  // The caller's `currentlyVisible` parameter is the visual truth (it
  // already factored in route-default + explicit-state). We just flip it:
  // visible → close all; not visible → open this one.
  const ALL_COLLAPSED = "__none__";
  const [expandedKey, setExpandedKey] = useState(null);
  const toggleGroup = (toKey, currentlyVisible) => {
    setExpandedKey(currentlyVisible ? ALL_COLLAPSED : toKey);
  };

  // Reset the manually-toggled state on every navigation. Without this,
  // a user who chevron-peeked Group A while sitting in Group B's pages
  // could navigate into Group C and end up with Group A still showing as
  // expanded (stale `expandedKey`) and Group C collapsed even though the
  // user is now inside it. Resetting to `null` lets the route-active
  // default (`isOn`) take over again — which guarantees exactly ONE
  // group is open at a time: the one matching the current URL.
  //
  // The chevron-peek + collapse-self UX still work because neither
  // changes the pathname, so this effect doesn't fire mid-toggle.
  useEffect(() => {
    setExpandedKey(null);
  }, [location.pathname]);

  // Shared helper used in both rendering paths (group headers + parent
  // nav items with children). Keeps the 3-state resolution in one place.
  const resolveExpanded = (thisKey, routeActive) => {
    if (expandedKey === ALL_COLLAPSED) return false;
    if (expandedKey === null) return routeActive;
    return expandedKey === thisKey;
  };

  useEffect(() => {
    if (role === "buyer" || role === "seller") {
      const loadApps = () => api.get("/apps").then(r => {
        const ids = r.data.filter(a => a.installed).map(a => a.id);
        setInstalledApps(ids);
        // Track apps marked coming-soon for the current role so we can hide
        // them from the sidebar even if a stale install record exists.
        const cs = r.data.filter(a =>
          a.coming_soon === true ||
          (Array.isArray(a.coming_soon_for) && a.coming_soon_for.includes(role))
        ).map(a => a.id);
        setComingSoonAppIds(cs);
      }).catch(() => {});
      loadApps();
      // Listen for the App Store's install/uninstall events so the sidebar
      // updates immediately — no need to navigate or refresh the page.
      const onAppsChanged = () => loadApps();
      window.addEventListener("apps-changed", onAppsChanged);
      // Seller-only: hydrate the seller-arranges-shipping flag so the
      // conditional Shipments sidebar entry renders without a page refresh.
      // We re-fetch on `tenant-settings-saved` (fired by SellerSettingsPanels
      // when the Operations panel persists changes) so the sidebar reflects
      // the toggle within ~1 frame of saving.
      let onTenantSaved;
      if (role === "seller") {
        const loadShipFlag = () => api.get("/tenant/me").then(r => {
          const on = !!(((r?.data?.settings) || {}).profile?.offer_seller_arranged_shipping);
          setSellerArrangesShipping(on);
        }).catch(() => {});
        loadShipFlag();
        onTenantSaved = () => loadShipFlag();
        window.addEventListener("tenant-settings-saved", onTenantSaved);
      }
      // Sidebar counts (refresh on every mount = on every page load — exactly
      // what the user asked for: live updates on refresh, no polling overhead).
      api.get("/sidebar-counts").then(r => setCounts(r.data || {})).catch(() => {});
      // Messages unread badge — short poll so the count reflects inbound
      // messages without waiting for a full page reload.
      const loadUnread = () =>
        api.get("/chat/unread-count").then(r => setMessagesUnread(r.data?.unread || 0)).catch(() => {});
      loadUnread();
      const id = setInterval(loadUnread, 15000);
      return () => {
        clearInterval(id);
        window.removeEventListener("apps-changed", onAppsChanged);
        if (onTenantSaved) window.removeEventListener("tenant-settings-saved", onTenantSaved);
      };
    }
    if (role === "admin") {
      // Live open-ticket count for the Support sidebar badge.
      const loadOpen = () => api.get("/admin/support/tickets/open-count")
        .then(r => setSupportOpen(r.data?.open || 0)).catch(() => {});
      loadOpen();
      const id = setInterval(loadOpen, 30000);
      return () => clearInterval(id);
    }
  }, [role, supplier?.active?.id, location.pathname]);

  // ── Per-seller storefront mode ──────────────────────────────────────────
  // When the buyer is "inside" a seller (active supplier picked), the portal
  // feels like that seller's storefront: verqo Hub items, Browse Apps, and
  // cross-seller apps (Suppliers) are hidden; visible apps are filtered to
  // the ones THIS seller has enabled. Buyer leaves the storefront via the
  // existing supplier-switcher dropdown to return to the unified hub.
  const [supplierAppIds, setSupplierAppIds] = useState(null);
  useEffect(() => {
    if (role !== "buyer" || !supplier?.active?.tenant_owner_user_id) {
      setSupplierAppIds(null);
      return;
    }
    api.get("/buyer/supplier-features").then(r => {
      const sid = supplier.active.tenant_owner_user_id;
      const hit = (r.data?.supplier_apps || []).find(s => s.seller_id === sid);
      setSupplierAppIds(hit ? hit.app_ids || [] : []);
    }).catch(() => setSupplierAppIds([]));
  }, [role, supplier?.active?.tenant_owner_user_id]);

  const handleLogout = async () => {
    // If we're in a /demo session, return to /demo instead of the main login.
    // Use a hard navigation (window.location) to avoid RoleRoute's <Navigate>
    // redirect firing when user state clears and routing us to /login first.
    const fromDemo = sessionStorage.getItem("app_mode_override") === "demo";
    const target = fromDemo ? "/demo" : "/";
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("token");
    sessionStorage.removeItem("app_mode_override");
    window.location.replace(target);
  };

  // Hub mode = buyer has no active supplier picked. Affects how we render
  // some nav items: they stay in place (predictable layout) but appear muted
  // with a hover tooltip telling the user what unlocks them.
  const hubMode = role === "buyer" && !supplier?.active;

  // ── Admin group renderer (iter-76 collapsible nav) ────────────────────
  // Differs from renderItem in that the parent itself is NOT a route — it's
  // just a collapsible group header. Children are full NavLinks to their
  // own routes (vs. buyer/seller children which hash into the same route).
  const renderAdminGroup = (g) => {
    if (g.is_single) {
      // Render the single-entry "Platform" home shortcut as a top-level row.
      return (
        <NavLink
          key={g.id}
          to={g.to}
          end={g.end}
          data-testid={`nav-${g.id}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg pl-3 pr-3 py-2 text-sm transition-colors ${
            isActive ? "bg-foreground text-background"
                     : "text-foreground/70 hover:bg-secondary hover:text-foreground"
          }`}>
          <g.icon className="w-4 h-4"/>
          <span className="flex-1">{g.label}</span>
        </NavLink>
      );
    }
    // A group has child routes — render as collapsible.
    const childPaths = (g.children || []).map(c => c.to);
    const isOn = childPaths.some(p =>
      location.pathname === p || location.pathname.startsWith(p + "/")
    );
    const expandKey = `__group_${g.id}`;
    const isExpanded = resolveExpanded(expandKey, isOn);
    // Group-level child badge sum (currently used for support tickets).
    const totalBadge = (g.children || []).reduce((sum, c) => {
      if (c.badge_key === "support_open") return sum + (supportOpen || 0);
      return sum;
    }, 0);
    return (
      <div key={g.id} data-testid={`nav-group-${g.id}`}>
        <button
          type="button"
          onClick={() => toggleGroup(expandKey, isExpanded)}
          data-testid={`nav-group-toggle-${g.id}`}
          aria-expanded={isExpanded}
          className={`group relative w-full flex items-center gap-3 rounded-lg pl-3 pr-9 py-2 text-sm transition-colors ${
            isOn ? "bg-foreground/8 text-foreground font-medium"
                 : "text-foreground/70 hover:bg-secondary hover:text-foreground"
          }`}>
          <g.icon className="w-4 h-4"/>
          <span className="flex-1 text-left">{g.label}</span>
          {totalBadge > 0 && (
            <span
              data-testid={`sidebar-badge-${g.id}`}
              className="min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-semibold grid place-items-center bg-accent text-foreground"
            >
              {totalBadge > 99 ? "99+" : totalBadge}
            </span>
          )}
          <ChevronDown
            className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground/50 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </button>
        {isExpanded && (
          <div className="mt-1 ml-4 pl-3 border-l border-border space-y-0.5"
               data-testid={`nav-group-children-${g.id}`}>
            {(g.children || []).map(c => {
              const childBadge = c.badge_key === "support_open" ? (supportOpen || 0) : 0;
              return (
                <NavLink
                  key={c.to}
                  to={c.to}
                  data-testid={`nav-${c.to.replace(/[^a-z0-9]/gi, "-").toLowerCase().replace(/^-/, "")}`}
                  className={({ isActive }) => `group flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors ${
                    isActive ? "bg-foreground text-background font-medium"
                             : "text-foreground/60 hover:text-foreground hover:bg-secondary/60"
                  }`}>
                  <c.icon className="w-3.5 h-3.5"/>
                  <span className="flex-1 truncate">{c.label}</span>
                  {childBadge > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold grid place-items-center bg-accent text-foreground">
                      {childBadge > 99 ? "99+" : childBadge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderItem = (m) => {
    // Parent is "on" if its own path matches OR if any of its `to:`-style
    // children (which navigate to separate routes) match the current path.
    // Hash-style children share the parent path and are handled separately.
    const childRouteHit = (m.children || []).some(c => c.to && (
      location.pathname === c.to || location.pathname.startsWith(c.to + "/")
    ));
    const isOn = location.pathname === m.to
              || (!m.end && location.pathname.startsWith(m.to + "/"))
              || childRouteHit;
    const hasChildren = Array.isArray(m.children) && m.children.length > 0;
    // Expanded state defaults to true when the route is active, but the user
    // can toggle the chevron to collapse it without navigating away.
    const isExpanded = resolveExpanded(m.to, isOn);
    const showChildren = hasChildren && isExpanded;
    const defaultHash = hasChildren ? m.children[0].hash : "";
    const currentHash = (location.hash || `#${defaultHash}`).replace("#", "");
    const muted = hubMode && m.disabledInHubMode && !isOn;
    // Seller setup lock — every non-settings sidebar item gets locked until
    // the seller finishes their setup checklist. The Settings entry itself
    // is rendered separately in the Account group and is never locked.
    const locked = setupLocked && m.to !== "/seller/settings";

    if (locked) {
      // Render a non-NavLink locked row that bounces to /seller/settings
      // when clicked. Children/badges suppressed — they have no meaning
      // until the gate is open.
      return (
        <div key={m.to}>
          <button
            type="button"
            onClick={() => nav("/seller/settings")}
            data-testid={`nav-${m.label.replace(/\s+/g, '-').toLowerCase()}-locked`}
            title="Finish your setup to unlock the portal"
            className="w-full flex items-center gap-3 rounded-lg pl-3 pr-3 py-2 text-sm transition-colors text-foreground/35 hover:bg-secondary/40 hover:text-foreground/55 cursor-not-allowed"
          >
            <m.icon className="w-4 h-4" />
            <span className="flex-1 text-left">{m.label}</span>
            <Lock className="w-3 h-3 opacity-60" />
          </button>
        </div>
      );
    }

    return (
      <div key={m.to}>
        <div className="relative">
          <NavLink
            to={m.to}
            end={m.end}
            data-testid={`nav-${m.label.replace(/\s+/g, '-').toLowerCase()}`}
            title={muted ? "Available once you connect a supplier" : undefined}
            onClick={() => {
              // Whole-row click also toggles the dropdown. Navigation still
              // happens via NavLink's default behaviour — we don't preventDefault.
              if (hasChildren) {
                toggleGroup(m.to, isExpanded);
              }
            }}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg pl-3 ${hasChildren ? "pr-9" : "pr-3"} py-2 text-sm transition-colors ${
              isActive ? "bg-foreground text-background"
              : muted    ? "text-foreground/35 hover:bg-secondary/40 hover:text-foreground/55"
              :            "text-foreground/70 hover:bg-secondary hover:text-foreground"
            }`}
          >
            <m.icon className="w-4 h-4" />
            <span className="flex-1">{m.label}</span>
            {typeof m.badge === "number" && m.badge > 0 && (
              <span
                data-testid={`sidebar-badge-${m.label.replace(/\s+/g, '-').toLowerCase()}`}
                className={`min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-semibold grid place-items-center ${isOn ? "bg-background/20 text-background" : "bg-accent text-foreground"}`}
              >
                {m.badge > 99 ? "99+" : m.badge}
              </span>
            )}
          </NavLink>
          {hasChildren && (
            <button
              type="button"
              onClick={(e) => {
                // Chevron-only toggle — does NOT navigate. Use this if the
                // user wants to expand a different section without leaving the
                // current page.
                e.preventDefault(); e.stopPropagation();
                toggleGroup(m.to, isExpanded);
              }}
              data-testid={`nav-toggle-${m.label.replace(/\s+/g, '-').toLowerCase()}`}
              aria-label={`Toggle ${m.label}`}
              className={`absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md grid place-items-center transition-colors ${isOn ? "text-background hover:bg-background/15" : "text-foreground/50 hover:bg-secondary"}`}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showChildren ? "rotate-180" : ""}`}/>
            </button>
          )}
        </div>
        {showChildren && (
          <div className="mt-1 ml-4 pl-3 border-l border-border space-y-0.5" data-testid={`subnav-${m.label.toLowerCase()}`}>
            {m.children.map(c => {
              // Two flavors of children:
              //  • hash-style — stays on parent route, navigates to /parent#hash
              //  • route-style — navigates to its own `to:` (use when the child
              //    has its own dedicated page, e.g. /seller/invoices nested
              //    under Payouts but rendered as a standalone screen).
              const isRouteChild = !!c.to;
              const childPath = isRouteChild ? c.to : `${m.to}#${c.hash}`;
              const active = isRouteChild
                ? (location.pathname === c.to || location.pathname.startsWith(c.to + "/"))
                : (currentHash === c.hash);
              // Map sidebar parent path → count key prefix for the API.
              // /seller/orders → "orders" · /buyer/payments → "payments" · etc.
              const parentKey = m.to.split("/").pop() || "";
              const count = isRouteChild ? undefined : counts[`${parentKey}.${c.hash}`];
              const showCount = typeof count === "number" && count > 0;
              return (
                <NavLink key={c.hash || c.to || "default"} to={childPath}
                  end={c.end}
                  data-testid={`subnav-${c.hash || c.to?.split("/").pop() || "default"}`}
                  className={`group flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-colors ${active ? "bg-secondary text-foreground font-medium" : "text-foreground/55 hover:text-foreground hover:bg-secondary/60"}`}>
                  <c.icon className="w-3.5 h-3.5" />
                  <span className="flex-1 truncate">{c.label}</span>
                  {showCount && (
                    <span
                      data-testid={`subnav-count-${parentKey}-${c.hash}`}
                      className={`min-w-[20px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold tabular-nums grid place-items-center transition-colors ${
                        active
                          ? "bg-foreground text-background"
                          : "bg-foreground/8 text-foreground/80 group-hover:bg-foreground group-hover:text-background"
                      }`}
                      style={!active ? { backgroundColor: "rgba(0,0,0,0.07)" } : undefined}
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const { isVisible } = useAppMode();

  // Team-role-aware sidebar gating (Feb 2026, Flavor C):
  //   • money:    needs can_money     (Operators / Viewers can't see Payouts)
  //   • settings: needs can_settings  (Operators / Viewers can't see Settings)
  //   • banking:  needs can_banking   (sub-views in Settings handle their own gate)
  // Default-true for legacy users (solo owners without team_flags set).
  const canMoney    = user?.team_flags?.can_money    !== false;
  const canSettings = user?.team_flags?.can_settings !== false;
  const teamRole    = user?.team_role || "owner";
  const filterByFlags = (items) => items.filter(i => {
    if (i.money    && !canMoney)    return false;
    if (i.settings && !canSettings) return false;
    return true;
  });
  // Back-compat alias for legacy call sites further down.
  const filterMoney = filterByFlags;

  const renderSidebar = () => {
    if (role === "admin") {
      // Iter-76 reorganization: 9 collapsible groups (replacing the legacy
      // 35-tab flat list). The group whose child route matches the current
      // path is auto-expanded; user can toggle others via chevron.
      const visibleGroups = ADMIN_GROUPS
        .filter(g => !g.feature || isVisible(g.feature))
        .map(g => g.is_single ? g : {
          ...g,
          children: filterByFeature(g.children, isVisible),
        })
        .filter(g => g.is_single || (g.children && g.children.length > 0));
      return (
        <nav className="flex-1 p-3 overflow-y-auto" data-testid="admin-sidebar">
          <div className="space-y-1">
            {visibleGroups.map(g => renderAdminGroup(g))}
          </div>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2">Account</div>
          <div className="space-y-1">
            <HelpSupportButton/>
          </div>
        </nav>
      );
    }
    if (role === "seller") {
      // Seller sidebar: show only apps the seller has actually installed,
      // and filter out any flagged "coming soon" so the user never lands on
      // a dead route from an old/stale install record.
      const apps = filterByFeature(
        APPS_SELLER.filter(a => installedApps.includes(a.app_id) && !comingSoonAppIds.includes(a.app_id)),
        isVisible
      ).map(a => a.app_id === "messages" ? { ...a, badge: messagesUnread } : a);
      const core = filterMoney(filterByFeature(CORE_SELLER, isVisible));
      // Inject the optional "Shipments" tab after Orders when the seller
      // has enabled `offer_seller_arranged_shipping` in Settings → Operations.
      // Most sellers don't ship themselves (buyer-arranges is the wholesale
      // default), so we keep it OFF by default — opt-in only.
      if (sellerArrangesShipping) {
        const insertAt = core.findIndex(it => it.to === "/seller/orders");
        const shipmentsEntry = {
          to: "/seller/shipping",
          // Iter 246 · label unified with buyer portal ("Shipping" — same
          // section name in both sidebars for parity).
          label: "Shipping",
          icon: Truck,
          feature: "seller_dashboard",
        };
        if (insertAt >= 0) core.splice(insertAt + 1, 0, shipmentsEntry);
        else               core.push(shipmentsEntry);
      }
      const settings = filterByFlags(filterByFeature(SETTINGS_SELLER, isVisible));
      // Subscription suspended → collapse sidebar to a single "Reactivate"
      // link. Read-only state stays fully navigable (dashboard banner is
      // the canonical UX surface for that state).
      if (billingLocked) {
        return (
          <nav className="flex-1 p-3 overflow-y-auto" data-testid="sidebar-billing-locked">
            <div className="mx-1 mb-3 px-3 py-3 rounded-lg border border-rose-300 bg-rose-50 text-[11.5px] leading-snug">
              <div className="flex items-center gap-1.5 mb-1">
                <Lock className="w-3 h-3 text-rose-700"/>
                <span className="font-medium text-rose-900">Account suspended</span>
              </div>
              <div className="text-rose-900/70">
                Your verqo wallet ran short and the dunning window expired. Reactivate
                to unlock the portal.
              </div>
            </div>
            <NavLink
              to="/seller/settings#plan-billing"
              data-testid="sidebar-reactivate-link"
              className={({ isActive }) => `mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-rose-700 text-white" : "bg-rose-100 text-rose-900 hover:bg-rose-200"}`}>
              <Lock className="w-4 h-4"/>
              <span className="flex-1 text-left">Reactivate verqo</span>
            </NavLink>
            <div className="px-3 mt-6">
              <HelpSupportButton/>
            </div>
          </nav>
        );
      }
      return (
        <nav className="flex-1 p-3 overflow-y-auto">
          {setupLocked && (
            <div
              data-testid="sidebar-setup-locked-banner"
              className="mx-1 mb-3 px-3 py-2.5 rounded-lg border border-accent/40 bg-accent/10 text-[11.5px] leading-snug"
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Lock className="w-3 h-3 text-foreground/70"/>
                <span className="font-medium text-foreground">Finish setup to unlock</span>
              </div>
              <div className="text-foreground/65">
                Complete your settings to start taking orders.
              </div>
            </div>
          )}
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-2 mb-2">Core</div>
          <div className="space-y-1">{core.map(renderItem)}</div>
          {apps.length > 0 && <>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2 flex items-center justify-between">
            <span>Apps <span className="ml-1 text-foreground/30">({apps.length})</span></span>
          </div>
          <div className="space-y-1">{apps.map(renderItem)}</div>
          </>}
          {isVisible("addons_store") && (
          setupLocked ? (
            <button
              type="button"
              onClick={() => nav("/seller/settings")}
              data-testid="nav-app-store-locked"
              title="Finish your setup to unlock the portal"
              className="mt-1 w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors text-foreground/35 hover:bg-secondary/40 hover:text-foreground/55 cursor-not-allowed"
            >
              <AppWindow className="w-4 h-4" />
              <span className="flex-1 text-left">Browse apps</span>
              <Lock className="w-3 h-3 opacity-60"/>
            </button>
          ) : (
            <NavLink to="/seller/apps"
              data-testid="nav-app-store"
              className={({ isActive }) => `mt-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-foreground text-background" : "text-foreground/50 hover:bg-secondary hover:text-foreground"}`}>
              <AppWindow className="w-4 h-4" />Browse apps
            </NavLink>
          )
          )}
          {settings.length > 0 && <>
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2">Account</div>
          <div className="space-y-1">
            {settings.map(renderItem)}
            <HelpSupportButton/>
          </div>
          </>}
        </nav>
      );
    }
    // buyer
    const inStorefront = !!supplier?.active;
    const sellerName = supplier?.active?.tenant_name || "supplier";

    // App-visibility rules (Feb 2026 buyer-apps governance · 3 rules):
    //   R1 — STOREFRONT: buyer is a guest inside a seller's portal. They
    //        can ONLY see apps the seller has pushed to them. No buyer-only
    //        apps. No "Browse apps". No install/uninstall. Catalog/Messages/
    //        Dropship/RFQ require the seller to have enabled them; if the
    //        seller hasn't, the buyer never sees them. Suppliers (cross-
    //        seller directory) is always hidden in storefront.
    //   R2 — HUB: buyer's own account, no active supplier. This is the
    //        only place where Browse Apps lives.
    //   R3 — HUB: the sidebar "Apps" group used to be suppressed while
    //        installs were globally locked (BUYER_APPS_INSTALL_LOCKED env).
    //        We now relax this to: show the group when the buyer has at
    //        least one cross-portal app installed (Catalog/Messages/
    //        Dropship/RFQ — pushed by a connected seller). This lets a
    //        buyer with an active supplier reach Catalog from the Hub
    //        sidebar without first jumping into the storefront. Buyers
    //        with no cross-portal app still see no group (same as before).
    const hasCrossPortalAppInstalled = installedApps.some(id => CROSS_PORTAL_APP_IDS.has(id));
    const installLocked = !hasCrossPortalAppInstalled;
    const storefrontApps = inStorefront
      ? APPS_BUYER.filter(a => {
          // R1 — only seller-pushed apps. Suppliers + buyer-only apps gone.
          if (a.app_id === "suppliers") return false;
          if (comingSoonAppIds.includes(a.app_id)) return false;
          if (!CROSS_PORTAL_APP_IDS.has(a.app_id)) return false;
          return (supplierAppIds || []).includes(a.app_id);
        })
      : (installLocked
          // R3 — hide the apps group entirely in hub while installs are locked
          ? []
          : APPS_BUYER.filter(a => installedApps.includes(a.app_id) && !comingSoonAppIds.includes(a.app_id)));
    const apps = filterByFeature(storefrontApps, isVisible)
      .map(a => a.app_id === "messages" ? { ...a, badge: messagesUnread } : a);

    const core = filterMoney(filterByFeature(CORE_BUYER, isVisible));
    const hub  = inStorefront ? [] : filterMoney(filterByFeature(HUB_BUYER, isVisible));

    return (
      <nav className="flex-1 p-3 overflow-y-auto">
        {inStorefront ? (
          <button
            type="button"
            onClick={() => openProfile(supplier.active.tenant_slug)}
            data-testid="storefront-header-profile-link"
            className="text-[10px] mono uppercase tracking-widest text-foreground/55 hover:text-foreground px-3 mt-2 mb-2 flex items-center gap-1.5 group w-full text-left"
            title="View seller profile"
          >
            <span className="truncate">{sellerName}</span>
            <Eye className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"/>
          </button>
        ) : (
          <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-2 mb-2">Core</div>
        )}
        <div className="space-y-1">{core.map(renderItem)}</div>
        {hub.length > 0 && <>
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2">verqo Hub</div>
        <div className="space-y-1">{hub.map(renderItem)}</div>
        </>}
        {apps.length > 0 && <>
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2 flex items-center justify-between">
          <span>Apps <span className="ml-1 text-foreground/30">({apps.length})</span></span>
        </div>
        <div className="space-y-1">{apps.map(renderItem)}</div>
        </>}
        {/* Browse apps store + cross-seller catalogs only visible from the
            unified verqo Hub. Inside a seller storefront they're hidden so
            the buyer stays focused on that supplier's experience. */}
        {!inStorefront && isVisible("addons_store") && (
        <NavLink to="/buyer/apps"
          data-testid="nav-app-store"
          className={({ isActive }) => `mt-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-foreground text-background" : "text-foreground/50 hover:bg-secondary hover:text-foreground"}`}>
          <AppWindow className="w-4 h-4" />Browse apps
        </NavLink>
        )}
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 px-3 mt-5 mb-2">Account</div>
        <div className="space-y-1">
          <NavLink to="/buyer/settings" data-testid="nav-settings"
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-foreground text-background" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}>
            <SettingsIcon className="w-4 h-4" />Settings
          </NavLink>
          <HelpSupportButton/>
        </div>
      </nav>
    );
  };

  return (
    <div
      className="min-h-screen flex bg-background"
      style={activeSupplier ? {
        "--supplier-primary": branding.primary_color || "#0a1f1c",
        "--supplier-accent":  branding.accent_color  || "#e8ff3a",
      } : {}}
    >
      <aside
        className="w-64 border-r border-border bg-white/60 backdrop-blur flex flex-col sticky top-0 h-screen"
        data-testid="portal-sidebar"
        data-mobile-open={mobileNavOpen ? "true" : "false"}
      >
        <div className="px-6 py-5 border-b border-border">
          <div className="flex items-center gap-2" data-testid="portal-brand-mark">
            {activeSupplier ? (
              <>
                {/*
                  Sidebar brand chip. The 32×32 square uses the square
                  monogram when uploaded; otherwise renders the seller's
                  initial on the primary color so a wide horizontal logo
                  doesn't get squished into a thin sliver inside an
                  8px-tall slot.
                */}
                <div
                  className="w-8 h-8 rounded-md grid place-items-center font-bold display text-sm overflow-hidden shrink-0"
                  style={{ background: branding.primary_color || "#0a1f1c", color: branding.accent_color || "#e8ff3a" }}
                >
                  {branding.logo_square_url
                    ? <img src={branding.logo_square_url} alt={activeSupplier.tenant_name} className="w-full h-full object-cover"/>
                    : activeSupplier.tenant_name.slice(0,1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="display font-semibold leading-tight truncate">{activeSupplier.tenant_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-foreground/50">Wholesale Portal</div>
                </div>
              </>
            ) : (
              <>
                {/* Square brand chip — mirrors the favicon (checkmark V
                   on `#0c1612`). `shrink-0` is critical; without it the
                   flex layout squeezes the box into a tall pill when the
                   adjacent label expands. */}
                <VerqoMark className="w-9 h-9 rounded-md overflow-hidden"/>
                <div>
                  <div className="display font-semibold leading-tight">verqo</div>
                  <div className="text-[10px] uppercase tracking-widest text-foreground/50">{LABEL[role]}</div>
                </div>
              </>
            )}
          </div>
        </div>
        {renderSidebar()}
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2" data-testid="sidebar-identity">
            {role === "admin" ? (
              <>
                <div className="text-sm font-medium truncate" data-testid="sidebar-admin-name">{user?.name}</div>
                <div className="text-xs text-foreground/60 truncate">Administrator</div>
              </>
            ) : (
              <div className="text-sm font-medium truncate" data-testid="sidebar-company-name">
                {user?.company || user?.name}
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="w-full justify-start text-foreground/70 hover:text-foreground">
            <LogOut className="w-4 h-4 mr-2" />Sign out
          </Button>
        </div>
      </aside>

      {/* Iter 218 · mobile drawer backdrop — sits behind the fixed
          <aside> when open; clicking dismisses the drawer. */}
      {mobileNavOpen && (
        <div
          data-testid="mobile-sidebar-backdrop"
          data-mobile-open="true"
          onClick={() => setMobileNavOpen(false)}
          onTouchStart={() => setMobileNavOpen(false)}
          aria-hidden="false"
        />
      )}

      <main className={`flex-1 min-w-0 ${role === "seller" ? "seller-has-bell" : ""}`}>
        <TestModeBanner />
        <ModeBanner />
        {role === "admin" && <EnvBanner />}
        {role === "admin" && (
          <div className="border-b border-border bg-background/70 backdrop-blur sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-secondary"
              aria-label="Open navigation"
              data-testid="mobile-nav-toggle"
            >
              <Menu className="w-4 h-4"/>
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("open-command-palette"))}
              data-testid="admin-cmdk-trigger"
              className="group inline-flex items-center gap-2 h-9 px-3 rounded-full bg-secondary/60 hover:bg-secondary text-foreground/65 hover:text-foreground border border-border transition-colors min-w-[200px] sm:min-w-[280px] max-w-md flex-1"
              aria-label="Open command palette"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-foreground/50"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <span className="text-[12.5px] flex-1 text-left">Search admin…</span>
              <kbd className="text-[10px] mono px-1.5 py-0.5 rounded border border-border bg-background/70 text-foreground/60 shrink-0">
                {typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? "⌘K" : "Ctrl K"}
              </kbd>
            </button>
            <NotificationsBell/>
          </div>
        )}
        {role === "buyer" && (
          <div
            className="border-b border-border bg-background/70 backdrop-blur sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3"
            style={activeSupplier ? { borderBottomColor: (branding.accent_color || "#e8ff3a") + "40" } : {}}
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-secondary"
                aria-label="Open navigation"
                data-testid="mobile-nav-toggle"
              >
                <Menu className="w-4 h-4"/>
              </button>
              {!activeSupplier && (
                <span data-testid="hub-mode-pill"
                  className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-secondary text-foreground/65 text-[11px] mono uppercase tracking-[0.15em]">
                  <Home className="w-3 h-3"/>Hub mode
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <SupplierSwitcher/>
              <NotificationsBell/>
            </div>
          </div>
        )}
        {role === "seller" && (
          <>
            {/* Mobile-only compact bar — hamburger + bell.
                Desktop skips this entirely to reclaim ~60px above the
                fold (user feedback iter 221). Bell is floated instead. */}
            <div className="md:hidden border-b border-border bg-background/70 backdrop-blur sticky top-0 z-30 px-4 py-2 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border hover:bg-secondary"
                aria-label="Open navigation"
                data-testid="mobile-nav-toggle"
              >
                <Menu className="w-4 h-4"/>
              </button>
              <NotificationsBell/>
            </div>
            {/* Desktop-only floating bell — sits over the page header. */}
            <div className="hidden md:block fixed top-3 right-4 lg:right-6 z-40" data-testid="seller-desktop-bell">
              <NotificationsBell/>
            </div>
          </>
        )}
        <Outlet />
      </main>
      <CommandPalette user={user}/>
      {/* Iter 154 · KYC gate — renders a fullscreen blocker on top of the
          shell for any user whose `GET /me/kyc-gate` returns required:true.
          Demo accounts, legacy (grandfathered) users, and verified accounts
          render `children` (null here = no-op). */}
      <KycGateBlocker roleHint={role}>{null}</KycGateBlocker>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="border-b border-border px-4 sm:px-6 lg:px-8 py-3 lg:py-4 bg-background/50 backdrop-blur sticky top-0 z-20 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between xl:gap-4">
      <div className="min-w-0">
        <h1 className="display text-xl md:text-2xl">{title}</h1>
        {subtitle && <p className="text-sm text-foreground/60 mt-0.5">{subtitle}</p>}
      </div>
      {action && (
        <div className="-mx-4 sm:-mx-6 lg:mx-0 xl:min-w-0 overflow-x-auto no-scrollbar">
          <div className="flex items-center px-4 sm:px-6 lg:px-0 [&_button]:whitespace-nowrap [&_a]:whitespace-nowrap">
            {action}
          </div>
        </div>
      )}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = "default", testid, icon: Icon }) {
  const t = tone === "ink" ? "card-ink" : "bg-white border border-border";
  return (
    <div className={`rounded-2xl p-5 relative ${t}`} data-testid={testid}>
      {Icon && <Icon className={`absolute top-5 right-5 w-4 h-4 ${tone==="ink"?"text-accent/60":"text-foreground/30"}`} />}
      <div className={`text-xs uppercase tracking-widest ${tone === "ink" ? "text-accent/80" : "text-foreground/50"}`}>{label}</div>
      <div className="display text-3xl mt-2">{value}</div>
      {hint && <div className={`text-xs mt-1 ${tone === "ink" ? "text-background/70" : "text-foreground/60"}`}>{hint}</div>}
    </div>
  );
}
